import React, { useEffect, useState } from "react";
import { Sparkles, ArrowUpRight, RefreshCw, Clock } from "lucide-react";
import { callClaude } from "../lib/ai";
import { toast } from "../lib/utils";
import { useProject } from "../state/ProjectContext";

export function AIPanel({ title, section, buildPrompt, buttonLabel = "Generate" }) {
  const { doc, mutate, readOnly } = useProject();
  const stored = doc?.aiOutputs?.[section];

  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState(stored?.text || "");
  const [generatedAt, setGeneratedAt] = useState(stored?.at || "");
  const [err, setErr] = useState("");

  // Reload persisted output if project or section changes.
  useEffect(() => {
    const s = doc?.aiOutputs?.[section];
    setOut(s?.text || "");
    setGeneratedAt(s?.at || "");
    setErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, section]);

  const run = async () => {
    setErr(""); setLoading(true);
    try {
      const txt = await callClaude(buildPrompt());
      const ts = new Date().toISOString();
      setOut(txt);
      setGeneratedAt(ts);
      if (!readOnly) {
        mutate(d => ({
          ...d,
          aiOutputs: { ...(d.aiOutputs || {}), [section]: { text: txt, at: ts } },
        }));
      }
    } catch (e) {
      setErr(e.message || "Failed");
      toast("AI: " + (e.message || "failed"));
    } finally {
      setLoading(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  const hasOutput = out && !loading;

  return (
    <div className="ai-panel colorful p-4 mb-4 relative" data-testid={`ai-panel-${section}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-7 h-7 rounded ai-chip flex items-center justify-center shadow-sm">
          <Sparkles size={14} strokeWidth={2} />
        </div>
        <div className="font-mono text-[11px] tracking-[0.1em] uppercase text-zinc-700 font-medium">{title}</div>
        {generatedAt && (
          <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1" data-testid={`ai-timestamp-${section}`}>
            <Clock size={10} strokeWidth={2}/> Generated {fmtTime(generatedAt)}
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={run} disabled={loading} data-testid={`ai-btn-${section}`}>
            {hasOutput ? <RefreshCw size={12}/> : null}
            {hasOutput ? "Regenerate" : buttonLabel}
            {!hasOutput ? <ArrowUpRight size={12} /> : null}
          </button>
        </div>
      </div>
      {loading && (
        <div className="mt-3 flex items-center gap-2 text-zinc-500 font-mono text-xs">
          <span className="dot-pulse"><span/><span/><span/></span>
          <span>Claude is thinking…</span>
        </div>
      )}
      {err && !loading && (
        <div className="mt-3 text-xs font-mono text-red-700 bg-red-50 border border-red-500/20 rounded-sm p-3 whitespace-pre-wrap">{err}</div>
      )}
      {out && !loading && (
        <div className="mt-3 text-sm leading-relaxed text-zinc-700 bg-zinc-50 border border-[var(--border)] rounded-sm p-4 whitespace-pre-wrap max-h-72 overflow-y-auto"
             data-testid={`ai-output-${section}`}>{out}</div>
      )}
    </div>
  );
}
